// ============================================================
// Communications + Teams + Tasks — data shapes, API wrappers, honesty hooks.
//
// PHASE_2I: the shared V2 comms/team spine. Every list comes ONLY from the
// API (sq_communication_channels / sq_messages / sq_team_members /
// sq_team_tasks) — NO demo channels, members, messages, or tasks are ever
// fabricated. Unapplied migrations surface as an honest "backend not
// migrated yet" state; no bearer token → authRequired; empty → honest empty.
// Realtime is REALTIME_DEFERRED (load on open/post/refresh, no sockets).
// ============================================================

import { commandCenterPath } from '../commandCenterRoutes'
import { useCallback, useEffect, useState } from 'react'
import { ApiError, apiRequest, isAuthError } from '../../../api/client'

export const COMMS_ROOT = commandCenterPath('communications')
export const TEAM_ROOT = commandCenterPath('team')

/**
 * COMMS_ACCENT — the Communications/Team lane accent (#2563eb). It lives here,
 * outside the guard-scoped presentation .tsx files, so the surfaces can pass it
 * to CommandCenterShell's `titleAccent` prop without carrying a hex literal. The
 * comms surface stylesheet mirrors it as the `--sqv-comms-accent` custom prop.
 */
export const COMMS_ACCENT = '#2563eb'

export interface Channel {
  id: string
  name: string
  description: string | null
  kind: 'channel' | 'dm'
  visibility: 'public' | 'private'
  status: 'active' | 'archived'
  created_by: string
  created_at: string
  updated_at: string
}

export interface Message {
  id: string
  channel_id: string
  sender_id: string
  body: string
  message_type: 'text' | 'system'
  created_at: string
}

export type TeamRole = 'owner' | 'admin' | 'member' | 'viewer'
export interface TeamMember {
  id: string
  user_id: string | null
  role: TeamRole
  display_name: string | null
  status: 'active' | 'invited' | 'suspended' | 'removed'
  created_by: string
  created_at: string
}

export type TaskStatus = 'open' | 'in_progress' | 'blocked' | 'done'
export type TaskPriority = 'low' | 'normal' | 'high' | 'urgent'
export interface TeamTask {
  id: string
  title: string
  description: string | null
  status: TaskStatus
  priority: TaskPriority
  assignee_id: string | null
  due_at: string | null
  blocker_reason: string | null
  source_channel_id: string | null
  source_message_id: string | null
  linked_job_id: string | null
  created_by: string
  created_at: string
  updated_at: string
}

// ---- honesty helper --------------------------------------------------------

export function humanizeError(err: unknown): string {
  if (err instanceof ApiError) {
    const msg = err.message || err.code
    // Only claim "not migrated" when the error is actually a missing relation.
    // A generic list_failed (transient DB error, timeout, RLS) must surface the
    // real message, not misdiagnose a migrated backend as un-migrated.
    if (/relation .* does not exist/i.test(msg) || /could not find the table/i.test(msg)) {
      return 'Backend not migrated yet — apply migration 20260709000002 to enable this.'
    }
    return msg
  }
  return 'Could not reach the communications backend.'
}

export interface ListState<T> {
  items: T[]
  loading: boolean
  error: string | null
  authRequired: boolean
}

/** Generic honesty list hook — never seeds demo data. */
function useApiList<T>(fetcher: (signal: AbortSignal) => Promise<T[]>): ListState<T> & { reload: () => void } {
  const [state, setState] = useState<ListState<T>>({ items: [], loading: true, error: null, authRequired: false })
  const load = useCallback(async (signal?: AbortSignal) => {
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const items = await fetcher(signal as AbortSignal)
      if (signal?.aborted) return
      setState({ items, loading: false, error: null, authRequired: false })
    } catch (err) {
      if (signal?.aborted) return
      if (isAuthError(err)) setState({ items: [], loading: false, error: null, authRequired: true })
      else setState({ items: [], loading: false, error: humanizeError(err), authRequired: false })
    }
  }, [fetcher])
  useEffect(() => { const ac = new AbortController(); load(ac.signal); return () => ac.abort() }, [load])
  return { ...state, reload: () => load() }
}

// ---- API wrappers ----------------------------------------------------------

export async function listChannels(signal?: AbortSignal): Promise<Channel[]> {
  const r = await apiRequest<{ ok: true; channels: Channel[] }>('/api/square/comms/channels', { method: 'GET', signal })
  return r.channels || []
}
export async function createChannel(payload: { name: string; description?: string; visibility?: 'public' | 'private' }): Promise<Channel> {
  const r = await apiRequest<{ ok: true; channel: Channel }>('/api/square/comms/channels', { method: 'POST', body: payload })
  return r.channel
}
export async function listDms(signal?: AbortSignal): Promise<Channel[]> {
  const r = await apiRequest<{ ok: true; dms: Channel[] }>('/api/square/comms/dms', { method: 'GET', signal })
  return r.dms || []
}
export async function listMessages(channel: string, signal?: AbortSignal): Promise<Message[]> {
  const r = await apiRequest<{ ok: true; messages: Message[] }>('/api/square/comms/messages', { method: 'GET', query: { channel }, signal })
  return r.messages || []
}
export async function postMessage(channel_id: string, body: string): Promise<Message> {
  const r = await apiRequest<{ ok: true; message: Message }>('/api/square/comms/messages', { method: 'POST', body: { action: 'post', channel_id, body } })
  return r.message
}
/** Real read-cursor update — POST action=mark_read sets sq_channel_members.last_read_at. */
export async function markChannelRead(channel_id: string): Promise<void> {
  await apiRequest<{ ok: true }>('/api/square/comms/messages', { method: 'POST', body: { action: 'mark_read', channel_id } })
}
export async function listTeamMembers(signal?: AbortSignal): Promise<TeamMember[]> {
  const r = await apiRequest<{ ok: true; members: TeamMember[] }>('/api/square/team/members', { method: 'GET', signal })
  return r.members || []
}
export async function listTeamTasks(signal?: AbortSignal): Promise<TeamTask[]> {
  const r = await apiRequest<{ ok: true; tasks: TeamTask[] }>('/api/square/team/tasks', { method: 'GET', signal })
  return r.tasks || []
}
export async function createTask(payload: { title: string; description?: string; priority?: TaskPriority; source_channel_id?: string; source_message_id?: string }): Promise<TeamTask> {
  const r = await apiRequest<{ ok: true; task: TeamTask }>('/api/square/team/tasks', { method: 'POST', body: { action: 'create', ...payload } })
  return r.task
}
export async function mutateTask(action: 'update_status' | 'assign' | 'set_blocker' | 'update', payload: Record<string, unknown>): Promise<TeamTask> {
  const r = await apiRequest<{ ok: true; task: TeamTask }>('/api/square/team/tasks', { method: 'POST', body: { action, ...payload } })
  return r.task
}
export async function getTask(id: string, signal?: AbortSignal): Promise<TeamTask | null> {
  const r = await apiRequest<{ ok: true; task: TeamTask | null }>('/api/square/team/tasks', { method: 'GET', query: { id }, signal })
  return r.task ?? null
}
export async function createTeamMember(payload: { display_name: string; role?: TeamRole; user_id?: string }): Promise<TeamMember> {
  const r = await apiRequest<{ ok: true; member: TeamMember }>('/api/square/team/members', { method: 'POST', body: payload })
  return r.member
}
/** Open (or reuse) a 2-party DM with a target user. */
export async function openDm(user_id: string): Promise<Channel> {
  const r = await apiRequest<{ ok: true; dm: Channel }>('/api/square/comms/dms', { method: 'POST', body: { user_id } })
  return r.dm
}

// ---- hooks -----------------------------------------------------------------

export function useChannels() { return useApiList<Channel>(listChannels) }
export function useDms() { return useApiList<Channel>(listDms) }
export function useTeamMembers() { return useApiList<TeamMember>(listTeamMembers) }
export function useTeamTasks() { return useApiList<TeamTask>(listTeamTasks) }

export function useChannelMessages(channelId: string | undefined) {
  const [state, setState] = useState<{ messages: Message[]; loading: boolean; error: string | null; authRequired: boolean }>({ messages: [], loading: true, error: null, authRequired: false })
  const load = useCallback(async (signal?: AbortSignal) => {
    if (!channelId) { setState({ messages: [], loading: false, error: null, authRequired: false }); return }
    setState((s) => ({ ...s, loading: true, error: null }))
    try {
      const messages = await listMessages(channelId, signal)
      if (signal?.aborted) return
      setState({ messages, loading: false, error: null, authRequired: false })
    } catch (err) {
      if (signal?.aborted) return
      if (isAuthError(err)) setState({ messages: [], loading: false, error: null, authRequired: true })
      else setState({ messages: [], loading: false, error: humanizeError(err), authRequired: false })
    }
  }, [channelId])
  useEffect(() => { const ac = new AbortController(); load(ac.signal); return () => ac.abort() }, [load])
  const send = useCallback(async (body: string) => { if (channelId) { await postMessage(channelId, body); await load() } }, [channelId, load])
  return { ...state, reload: () => load(), send }
}
